import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const FAQSection = () => {
  const [faqs, setFaqs] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/faq`);
        setFaqs(response.data);
      } catch (error) {
        console.error('Error fetching FAQs:', error);
        // Fallback FAQs
        setFaqs([
          { question: "Wie funktioniert die Fotobox?", answer: "Unsere Fotobox ist kinderleicht zu bedienen. Einfach vor die Kamera stellen, auf den Auslöser drücken und schon wird das Foto aufgenommen." },
          { question: "Wie lange kann ich die Fotobox mieten?", answer: "Die Standardmietdauer beträgt einen Abend/Tag. Bei längeren Veranstaltungen sprechen Sie uns einfach an." },
          { question: "Welche Accessoires sind dabei?", answer: "Bei jedem Paket ist ein Set von lustigen Accessoires dabei: Hüte, Brillen, Schnurrbärte und vieles mehr." },
          { question: "Kann ich ein eigenes Logo haben?", answer: "Ja! Für 50€ Aufpreis können wir Ihr Logo auf allen Bildern platzieren." },
          { question: "Wie erhalte ich die Bilder?", answer: "Alle Bilder werden auf einem USB-Stick gespeichert oder können als Download bereitgestellt werden." },
          { question: "In welchem Gebiet seid ihr verfügbar?", answer: "Wir sind in der Region Bodensee, Singen, Radolfzell, Konstanz und Stockach aktiv." },
        ]);
      }
    };
    fetchFAQs();
  }, []);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-16 md:py-20 px-6 bg-white" data-testid="faq-section">
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#3ECDC3] font-semibold text-sm tracking-widest uppercase block mb-4">
            Fragen & Antworten
          </span>
          <h2 
            className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900"
            
            data-testid="faq-headline"
          >
            Häufig <span className="text-[#3ECDC3]">gefragt</span>
          </h2>
        </motion.div>

        {/* FAQ Accordion */}
        <div className="space-y-4" data-testid="faq-list">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="border border-gray-200 rounded-xl overflow-hidden hover:border-[#3ECDC3]/50 transition-colors"
              data-testid={`faq-item-${index}`}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-gray-50 transition-colors"
                aria-expanded={openIndex === index}
                data-testid={`faq-button-${index}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#3ECDC3]/10 flex items-center justify-center flex-shrink-0">
                    <HelpCircle className="w-5 h-5 text-[#3ECDC3]" />
                  </div>
                  <span className="text-gray-900 font-medium pr-4">{faq.question}</span>
                </div>
                <ChevronDown 
                  className={`w-5 h-5 text-[#3ECDC3] flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="p-6 pt-0 bg-white">
                  <p className="text-gray-600 leading-relaxed pl-14">{faq.answer}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 text-center"
        >
          <p className="text-gray-500 mb-4">Noch Fragen? Wir helfen gerne!</p>
          <button
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center gap-2 text-[#3ECDC3] hover:text-[#2BB8AF] transition-colors font-semibold cursor-pointer"
            data-testid="faq-contact-link"
          >
            Kontaktieren Sie uns
            <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};
